import { useEffect, useState } from "react"
import { ArrowUpRight, Bot, CircleAlert, CircleCheck, LoaderCircle, Mail, ShieldCheck } from "lucide-react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { BrandLogo } from "@/components/brand-logo"
import { ThemeToggle } from "@/components/theme-toggle"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ApiError, api, type AuthUser, type ChatbotInvitationPreview } from "@/lib/api"
import { CHATBOT_SECTION_META, orderedChatbotSections } from "@/lib/chatbot-sections"

export function InvitePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")?.trim() ?? ""
  const [invitation, setInvitation] = useState<ChatbotInvitationPreview>()
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string>()
  const [accepted, setAccepted] = useState(false)
  const [destination, setDestination] = useState("/")

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setError("Este link de convite está incompleto.")
      setLoading(false)
      return () => { cancelled = true }
    }
    void Promise.all([
      api.getChatbotInvitation(token),
      api.me().catch(() => null),
    ]).then(([nextInvitation, user]) => {
      if (cancelled) return
      setInvitation(nextInvitation)
      setCurrentUser(user)
    }).catch((requestError) => {
      if (!cancelled) setError(requestError instanceof Error ? requestError.message : "Não foi possível carregar este convite.")
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [token])

  async function accept() {
    setAccepting(true)
    setError(undefined)
    try {
      const result = await api.acceptChatbotInvitation(token)
      const section = orderedChatbotSections(result.permissions ?? [])[0]
      setDestination(section ? `/chatbots/${result.chatbotId}/${section}` : "/")
      setAccepted(true)
    } catch (requestError) {
      setError(requestError instanceof ApiError ? requestError.message : "Não foi possível aceitar o convite.")
    } finally {
      setAccepting(false)
    }
  }

  const loginUrl = `/login?invite=${encodeURIComponent(token)}`
  const signupUrl = `/login?mode=signup&invite=${encodeURIComponent(token)}`
  const emailMatches = Boolean(currentUser && invitation && currentUser.email.trim().toLowerCase() === invitation.email.trim().toLowerCase())
  const unavailable = invitation && invitation.status !== "pending"

  return (
    <div className="bg-background text-foreground soft-grid relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-5 font-sans transition-colors duration-500 sm:px-8">
      <div className="drift pointer-events-none absolute -left-40 -top-44 h-[30rem] w-[30rem] rounded-full bg-muted-foreground/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 -right-32 h-[28rem] w-[28rem] rounded-full bg-muted-foreground/10 blur-3xl" />
      <main className="relative z-10 w-full max-w-[460px]">
        <div className="mb-5 flex items-center justify-between"><Link to="/login" aria-label="BubbleDesk home"><BrandLogo /></Link><ThemeToggle /></div>
        <section className="bg-card rounded-[24px] border border-border p-6 transition-colors duration-500 sm:p-8">
          {loading ? (
            <div className="flex flex-col items-center py-8"><LoaderCircle className="size-6 animate-spin text-primary" aria-label="Carregando convite" /><p className="muted mt-3 text-[12px]">Carregando convite...</p></div>
          ) : error ? (
            <div className="text-center"><span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive"><CircleAlert className="size-6" /></span><h1 className="mt-5 font-display text-[26px] font-bold">Convite indisponível</h1><p className="muted mt-2 text-[13px] leading-5">{error}</p><Button asChild className="mt-6 h-11 w-full rounded-xl"><Link to="/login">Ir para o login<ArrowUpRight className="size-4" /></Link></Button></div>
          ) : accepted ? (
            <div className="text-center"><span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-success/10 text-success"><CircleCheck className="size-6" /></span><h1 className="mt-5 font-display text-[26px] font-bold">Convite aceito</h1><p className="muted mt-2 text-[13px] leading-5">Agora você tem acesso a este assistente.</p><Button className="mt-6 h-11 w-full rounded-xl" onClick={() => navigate(destination, { replace: true })}>Abrir assistente<ArrowUpRight className="size-4" /></Button></div>
          ) : (
            <>
              <div className="mb-6 text-center"><span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Bot className="size-6" /></span><p className="muted mt-4 text-[11px] font-semibold uppercase tracking-[0.18em]">Convite para assistente</p><h1 className="mt-2 font-display text-[26px] font-bold leading-tight">Você foi convidado</h1><p className="muted mt-2 text-[13px] leading-5">Você recebeu acesso ao assistente <strong className="text-foreground">{invitation?.chatbotName}</strong>.</p></div>
              <div className="field-bg flex items-center gap-3 rounded-xl border border-border px-3.5 py-3"><Mail className="size-4 shrink-0 text-muted-foreground" /><div className="min-w-0"><p className="text-[11px] text-muted-foreground">Convite enviado para</p><p className="truncate text-[13px] font-semibold">{invitation?.email}</p></div></div>
              {invitation?.permissions.length ? (
                <div className="mt-3 rounded-xl border border-border px-3.5 py-3">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground"><ShieldCheck className="size-3.5" />Seu acesso inclui</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">{invitation.permissions.map((section) => <Badge key={section} variant="outline" className="rounded-md border-border bg-muted/40 text-[10px] font-medium text-muted-foreground">{CHATBOT_SECTION_META[section].label}</Badge>)}</div>
                </div>
              ) : null}
              {unavailable ? <p className="mt-4 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2.5 text-[12px] text-warning">Este convite não está mais disponível.</p> : currentUser ? emailMatches ? <Button className="mt-5 h-11 w-full rounded-xl" onClick={() => void accept()} disabled={accepting}>{accepting && <LoaderCircle className="size-4 animate-spin" />}{accepting ? "Aceitando..." : "Aceitar convite"}<ArrowUpRight className="size-4" /></Button> : <p className="mt-4 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2.5 text-[12px] text-warning">Você está conectado como {currentUser.email}. Entre com o e-mail convidado para continuar.</p> : <div className="mt-5 grid gap-2"><Button asChild className="h-11 rounded-xl"><Link to={loginUrl}>Entrar para aceitar<ArrowUpRight className="size-4" /></Link></Button><Button asChild variant="outline" className="h-11 rounded-xl"><Link to={signupUrl}>Criar conta<ArrowUpRight className="size-4" /></Link></Button></div>}
              {currentUser && !emailMatches && <Button asChild variant="outline" className="mt-3 h-11 w-full rounded-xl"><Link to="/login">Trocar de conta</Link></Button>}
            </>
          )}
        </section>
        <p className="muted mt-4 text-center text-[12px]">Acesso protegido ao assistente BubbleDesk.</p>
      </main>
    </div>
  )
}
